/**
 * Card di un attestato nell'area partecipante: titolo del webinar, data, presenza, codice di
 * verifica in `code` con "Copia", stato con icona e parola, un solo bottone.
 */
import { Award, Check, CircleAlert, CircleX, Clock, Copy, Download } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { cx } from '../cx.js';
import { Badge } from './Badge.js';
import { Button } from './Button.js';
import { Card } from './Card.js';

export type StatoAttestatoCard = 'in_coda' | 'pronto' | 'errore' | 'revocato';

export interface TestiCertificateCard {
  scarica: string;
  inPreparazione: string;
  inPreparazioneTesto: string;
  pronto: string;
  errore: string;
  erroreTesto: string;
  revocato: string;
  revocatoTesto: string;
  codice: string;
  copia: string;
  copiato: string;
}

export const TESTI_CERTIFICATE_CARD: TestiCertificateCard = {
  scarica: 'Scarica l’attestato',
  inPreparazione: 'In preparazione',
  inPreparazioneTesto: 'Lo stiamo preparando: arriva anche via email.',
  pronto: 'Pronto',
  errore: 'Non ancora disponibile',
  erroreTesto: 'Qualcosa non ha funzionato: l’organizzatore è avvisato.',
  revocato: 'Revocato',
  revocatoTesto: 'Contatta l’organizzatore.',
  codice: 'Codice di verifica',
  copia: 'Copia',
  copiato: 'Copiato',
};

export interface CertificateCardProps {
  titolo: string;
  /** data già formattata (Europe/Rome) */
  data: string;
  /** es. "Presenza 2 h 24 min · 80%" */
  presenza: string;
  /** codice già formattato a gruppi (ABCD-EFGH-2345) */
  codice: string;
  stato: StatoAttestatoCard;
  /** URL firmato: presente solo se pronto e non revocato */
  url?: string | null;
  /** es. "Emesso il 13 gennaio 2027" */
  emesso?: string | null;
  /** `primary` solo per la card principale: un solo primario per schermata */
  variantePrimaria?: boolean;
  testi?: Partial<TestiCertificateCard>;
  className?: string;
  extra?: ReactNode;
}

export function CertificateCard({ titolo, data, presenza, codice, stato, url, emesso, variantePrimaria = false, testi: testiProp, className, extra }: CertificateCardProps) {
  const t = { ...TESTI_CERTIFICATE_CARD, ...testiProp };
  const [copiato, setCopiato] = useState(false);
  async function copia() {
    try {
      await navigator.clipboard.writeText(codice.replace(/-/g, ''));
      setCopiato(true);
      setTimeout(() => setCopiato(false), 2000);
    } catch {
      /* appunti non disponibili: il codice resta leggibile */
    }
  }
  const badge = {
    in_coda: { v: 'warning' as const, icona: Clock, testo: t.inPreparazione, spiegazione: t.inPreparazioneTesto },
    pronto: { v: 'success' as const, icona: Check, testo: t.pronto, spiegazione: null },
    errore: { v: 'error' as const, icona: CircleAlert, testo: t.errore, spiegazione: t.erroreTesto },
    revocato: { v: 'error' as const, icona: CircleX, testo: t.revocato, spiegazione: t.revocatoTesto },
  }[stato];
  return (
    <Card className={cx('flex flex-col gap-4', className)} data-stato={stato}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1 min-w-0">
          <p className="t-body-small text-ink-muted">{data}</p>
          <h3 className="t-title-3">{titolo}</h3>
          <p className="t-body-small text-ink-muted">{presenza}</p>
          {emesso && <p className="t-body-small text-ink-muted">{emesso}</p>}
        </div>
        <Award aria-hidden size={24} strokeWidth={1.75} className="text-accent shrink-0" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={badge.v} icon={badge.icona}>
          {badge.testo}
        </Badge>
        {badge.spiegazione && <span className="t-body-small text-ink-muted">{badge.spiegazione}</span>}
      </div>
      <div className="flex flex-col gap-1">
        <span className="t-label text-ink-muted">{t.codice}</span>
        <div className="flex flex-wrap items-center gap-2">
          <code className="t-code bg-surface-sunken rounded-md px-3 py-2">{codice}</code>
          <Button variant="discreet" icon={copiato ? Check : Copy} onClick={copia} aria-live="polite">
            {copiato ? t.copiato : t.copia}
          </Button>
        </div>
      </div>
      {extra}
      {stato === 'pronto' && url && (
        <div className="pt-1">
          <Button
            variant={variantePrimaria ? 'primary' : 'secondary'}
            icon={Download}
            onClick={() => window.open(url, '_blank', 'noopener')}
          >
            {t.scarica}
          </Button>
        </div>
      )}
    </Card>
  );
}
